import {
  TIPOS_COLISAO,
  colisao,
  velocidadeDoEvento,
  type QuadroCinematica,
  type TipoColisao,
} from '../dominio/didatico/timelineCinematica'
import { impactosAte } from '../dominio/didatico/impactosMedidos'
import {
  haContatoPelaTraseira,
  leituraDoTipoDeImpacto,
  temAlvoExterno,
  alvosDoTipo,
} from '../dominio/didatico/tiposDeImpacto'
import type { Ocupante } from '../dominio/didatico/ocupantes'
import type { ReacaoOcupante } from '../dominio/didatico/retencaoOcupante'
import { calcularDanoVeicular, type DanoVeicular } from '../dominio/didatico/danoVeicular'
import type { ConfiguracaoOutroVeiculo } from '../dominio/didatico/colisaoEntreVeiculos'
import { SeletorOutroVeiculo } from './cinematica/SeletorOutroVeiculo'
import { avaliarAtencaoCorporal } from '../dominio/didatico/atencaoCorporal'
import { classificarSequenciaDeImpactos } from '../dominio/didatico/sequenciaImpactos'
import { classificarEncarceramentos } from '../dominio/didatico/encarceramento'
import type { EstadoFisica } from '../fisica/useFisicaCarga'
import type {
  AprisionamentoMembro,
  EjecaoOcupante,
  ImpactoCarga,
  EstresseBiomecanicoOcupante,
} from '../fisica/solucionador'
import type { TonusOcupante } from '../dominio/didatico/tonusOcupante'
import {
  alvoImpacto,
  type TipoAlvoImpacto,
} from '../dominio/didatico/alvosImpacto'
import {
  calcularDeltaV,
  calcularPulsoAceleracao,
  avaliarSindromeCinto,
  type AvaliacaoSindromeCinto,
  obterCorrelacaoXABCDE,
} from '../dominio/didatico/metricasCinematica'
import {
  obterPistasAtivas,
  type PistaCena,
} from '../dominio/didatico/pistasCena'
import type {
  LadoImpactoLateral,
  TrajetoriaFrontal,
} from '../dominio/didatico/variacoesCinematica'
import { CardPistaCena } from './cinematica/CardPistaCena'
import { CardFormulaCinematica } from './cinematica/CardFormulaCinematica'
import { PainelMetricasXABCDE } from './cinematica/PainelMetricasXABCDE'
import { PainelInvestigadorCSI } from './cinematica/PainelInvestigadorCSI'
import { SeletorVariacoesCinematica } from './cinematica/SeletorVariacoesCinematica'
import {
  calcularForcaProjetilHumano,
  avaliarChoqueCranioCraniano,
  avaliarEjecaoParcialMembros,
} from '../dominio/didatico/dinamicaMulticorpos'

export type SecaoPainelCinematica =
  | 'evento'
  | 'fisica'
  | 'sobreposicoes'
  | 'resultados'
  | 'metricas'
  | 'investigador'

interface Props {
  /** Blocos exibidos; por padrão mantém o painel legado completo. */
  secoes?: readonly SecaoPainelCinematica[]
  tipoColisao: TipoColisao
  alvoImpacto: TipoAlvoImpacto
  /** velocidade do evento escolhida pelo aluno, em km/h */
  velocidadeImpacto: number
  quadro: QuadroCinematica
  ocupantes: readonly Ocupante[]
  /** ocupante cujo movimento o painel está lendo */
  ocupanteFoco: Ocupante | null
  reacaoFoco: ReacaoOcupante | null
  /** dispositivos SRS que o perfil declara E este tipo de colisão aciona */
  srsAcionados: readonly string[]
  mostrarVetores: boolean
  mostrarRastros: boolean
  mostrarEixoColuna: boolean
  mostrarPistas?: boolean
  /** física real para ocupantes e objetos */
  fisicaReal: boolean
  estadoFisica: EstadoFisica
  tonusOcupante: TonusOcupante
  /** leituras de estresse biomecânico resolvidas */
  estresseBiomecanico?: Record<string, EstresseBiomecanicoOcupante>
  /** primeiro impacto de cada carga, resolvido pela engine */
  impactos: readonly ImpactoCarga[]
  /** membros mantidos em zonas de retenção por contato sustentado */
  aprisionamentos: readonly AprisionamentoMembro[]
  /** ejeções MEDIDAS: o corpo terminou fora do envelope do veículo */
  ejecoes: readonly EjecaoOcupante[]
  aoAlternarFisica: (valor: boolean) => void
  aoEscolherTipo: (t: TipoColisao) => void
  aoEscolherAlvo: (t: TipoAlvoImpacto) => void
  aoEscolherVelocidade: (kmh: number) => void
  aoEscolherTonus: (tonus: TonusOcupante) => void
  aoAlternar: (chave: 'mostrarVetores' | 'mostrarRastros' | 'mostrarEixoColuna', valor: boolean) => void
  aoAlternarMostrarPistas?: (mostrar: boolean) => void
  // Fases 1 a 5
  ehEV?: boolean
  ehBlindado?: boolean
  aoAlternarBlindado?: (ativo: boolean) => void
  trajetoriaFrontal?: TrajetoriaFrontal
  aoAlterarTrajetoriaFrontal?: (t: TrajetoriaFrontal) => void
  smallOverlap?: boolean
  aoAlternarSmallOverlap?: (ativo: boolean) => void
  ladoLateral?: LadoImpactoLateral
  aoAlterarLadoLateral?: (lado: LadoImpactoLateral) => void
  pistas?: readonly PistaCena[]
  pistaSelecionada?: PistaCena | null
  aoSelecionarPista?: (id: string | null) => void
  pistasInspecionadas?: readonly string[]
  aoAplicarPresetMulticorpo?: (preset: 'projetil' | 'cranio' | 'ejecaoParcial') => void
  /** outro veículo (classe, velocidade, arranjo, ponto de contato) */
  outroVeiculo?: ConfiguracaoOutroVeiculo
  aoAlterarOutroVeiculo?: (parcial: Partial<ConfiguracaoOutroVeiculo>) => void
  /** leitura da colisão veículo × veículo (velocidade relativa, ΔV) */
  leituraEntreVeiculos?: string | null
  /** dano já resolvido pela cena (veículo × veículo usa a velocidade relativa) */
  danoVeicular?: DanoVeicular
  mostrarMapaDeformacao?: boolean
  aoAlternarMapaDeformacao?: (mostrar: boolean) => void
  /** carroceria opaca com dano estrutural em raio-X */
  lataSolida?: boolean
  aoAlternarLataSolida?: (ativa: boolean) => void
  aoVerReplay?: () => void
  aoExportarParaOficina?: () => void
}


export interface PossivelLesao {
  regiao: string
  titulo: string
  gravidade: 'critica' | 'grave' | 'moderada' | 'leve'
  descricao: string
}

export interface PontoAprisionamento {
  local: string
  regiaoCorpo: string
  probabilidade: 'alta' | 'moderada' | 'possivel'
  descricao: string
  condutaResgate: string
}

function obterPossiveisLesoes(
  tipoColisao: TipoColisao,
  ocupante: Ocupante | null,
  ocupantes: readonly Ocupante[],
  comCinto: boolean,
  velocidadeImpacto: number,
  sindromeCinto: AvaliacaoSindromeCinto | null,
  rolagemRad: number,
  leituraBio?: {
    nivelCervical: { rotulo: string; classificacao: string; descricao: string }
    estresseJoelhos: string
    estresseCotovelos: string
  },
): PossivelLesao[] {
  const lesoes: PossivelLesao[] = []
  const ehLateral = tipoColisao === 'lateral' || tipoColisao === 'lateralComCapotamento'
  const ehFrontal = tipoColisao === 'frontal' || tipoColisao === 'frontalComTraseira'
  const ehTraseira = tipoColisao === 'traseira' || tipoColisao === 'frontalComTraseira'
  const ehCapotamento =
    tipoColisao === 'capotamento' || tipoColisao === 'tombamento' || tipoColisao === 'lateralComCapotamento'

  // 1. Crânio e Face
  const choqueCraniano = avaliarChoqueCranioCraniano(tipoColisao, ocupantes)
  if (choqueCraniano.ocorre) {
    lesoes.push({
      regiao: 'Crânio e Face',
      titulo: 'Choque Crânio-Crânio Adjacente',
      gravidade: 'critica',
      descricao:
        'Impacto mútuo direto entre as cabeças dos ocupantes dianteiros pela aceleração lateral sem airbag central.',
    })
  } else if (!comCinto && ehFrontal) {
    lesoes.push({
      regiao: 'Crânio e Face',
      titulo: 'TCE Grave e Trauma Facial (Padrão Up-and-Over)',
      gravidade: 'critica',
      descricao:
        'Impacto violento do crânio e face contra o para-brisa e coluna dianteira por ausência de contenção pelo cinto.',
    })
  } else if (ehLateral) {
    lesoes.push({
      regiao: 'Crânio e Face',
      titulo: 'Traumatismo Cranioencefálico Lateral',
      gravidade: 'grave',
      descricao: 'Deslocamento lateral súbito da cabeça contra a coluna B ou estrutura da porta/vidro.',
    })
  } else if (ehCapotamento) {
    lesoes.push({
      regiao: 'Crânio e Face',
      titulo: 'Múltiplas Contusões Cranianas / Escoriações',
      gravidade: 'grave',
      descricao: 'Contatos repetidos da cabeça contra o teto rebaixado e laterais durante a rolagem do veículo.',
    })
  } else {
    lesoes.push({
      regiao: 'Crânio e Face',
      titulo: 'Concussão e Desaceleração Cefálica',
      gravidade: 'moderada',
      descricao: 'Desaceleração rápida da massa encefálica no interior da calota craniana.',
    })
  }

  // 2. Coluna Cervical
  if (ehTraseira) {
    lesoes.push({
      regiao: 'Coluna Cervical',
      titulo: 'Chicote Cervical Severo (Whiplash)',
      gravidade: 'critica',
      descricao:
        'Hiperextensão brusca seguida de hiperflexão violenta. Risco elevado de subluxação vertebral e trauma raquimedular.',
    })
  } else if (ehCapotamento) {
    lesoes.push({
      regiao: 'Coluna Cervical',
      titulo: 'Compressão Axial Cervical',
      gravidade: 'critica',
      descricao:
        'Carga vertical direta no vértex craniano contra o teto colapsado, com risco de fratura por compressão vertebral.',
    })
  } else if (ehLateral) {
    lesoes.push({
      regiao: 'Coluna Cervical',
      titulo: 'Inflexão Lateral Cervical Extrema',
      gravidade: 'grave',
      descricao:
        'Flexão lateral forçada com estiramento ligamentar contralateral e compressão ipsilateral de raízes nervosas.',
    })
  } else {
    lesoes.push({
      regiao: 'Coluna Cervical',
      titulo: 'Hiperflexão Cervical Brusca',
      gravidade: leituraBio?.nivelCervical.classificacao === 'critico' ? 'critica' : 'moderada',
      descricao:
        'Projeção anterior da cabeça retida pelo tronco, sobrecarregando ligamentos posteriores e vértebras C5-C7.',
    })
  }

  // 3. Tórax e Abdômen
  const temTraseiroSemCinto = ocupantes.some((o) => o.idAncora?.startsWith('traseiro') && o.retencao === 'semCinto')
  if (ehFrontal && temTraseiroSemCinto && ocupante?.idAncora === 'motorista') {
    const fProj = calcularForcaProjetilHumano(75, velocidadeImpacto)
    lesoes.push({
      regiao: 'Tórax e Tronco',
      titulo: 'Esmagamento Torácico Dorsal (Projétil Humano)',
      gravidade: 'critica',
      descricao: `O passageiro traseiro sem cinto atinge o encosto com ~${fProj.forcaImpactoKgf} kgf (${fProj.toneladasEquivalentes} t), esmagando o motorista contra o volante.`,
    })
  }

  if (!comCinto && ehFrontal) {
    lesoes.push({
      regiao: 'Tórax e Abdômen',
      titulo: 'Trauma Torácico Fechado por Volante / Painel',
      gravidade: 'critica',
      descricao:
        'Impacto direto da caixa torácica contra o volante: fratura de esterno, múltiplos arcos costais e risco de contusão miocárdica.',
    })
  } else if (sindromeCinto && (sindromeCinto.temSinalDoCinto || sindromeCinto.temSubmarining)) {
    lesoes.push({
      regiao: 'Tórax e Abdômen',
      titulo: sindromeCinto.temSubmarining ? 'Submarining / Efeito Mergulho' : 'Síndrome do Cinto de Segurança',
      gravidade: sindromeCinto.riscoFraturaChance ? 'critica' : 'grave',
      descricao:
        sindromeCinto.descricao ||
        'Equimose e contusão pela compressão da faixa torácica e subabdominal; risco de lesão em vísceras ocas e fratura de Chance.',
    })
  } else if (ehLateral) {
    lesoes.push({
      regiao: 'Tórax e Abdômen',
      titulo: 'Contusão Torácica e Abdominal Lateral',
      gravidade: 'grave',
      descricao:
        'Intrusão da porta sobre o gradil costal: fratura de costelas ipsilaterais, contusão pulmonar e trauma esplênico/hepático.',
    })
  }

  // 4. Pelve e Membros Inferiores
  if (ehFrontal) {
    lesoes.push({
      regiao: 'Membros Inferiores e Pelve',
      titulo: 'Fratura de Fêmur / Joelho / Quadril (Down-and-Under)',
      gravidade: 'grave',
      descricao:
        'Impacto dos joelhos contra o painel inferior transmitindo carga axial ao fêmur e acetábulo (luxação posterior de quadril); sobrecarga nos pés pelos pedais.',
    })
  } else if (ehLateral) {
    lesoes.push({
      regiao: 'Membros Inferiores e Pelve',
      titulo: 'Fratura de Pelve e Cabeça Femoral',
      gravidade: 'grave',
      descricao: 'Compressão e intrusão direta da porta e soleira sobre o anel pélvico e trocânter maior.',
    })
  }

  // 5. Membros Superiores
  const rolGraus = Math.round((rolagemRad * 180) / Math.PI)
  const ejecao = avaliarEjecaoParcialMembros(tipoColisao, rolGraus)
  if (ejecao.ocorre) {
    lesoes.push({
      regiao: 'Membros Superiores',
      titulo: 'Risco de Ejeção Parcial de Membros',
      gravidade: 'critica',
      descricao:
        'Projeção do braço para fora da janela durante o capotamento/tombamento, com risco de desluvamento ou esmagamento sob a lataria.',
    })
  }

  return lesoes
}

function obterPontosAprisionamento(
  tipoColisao: TipoColisao,
  ocupante: Ocupante | null,
  danoNivel: 'leve' | 'moderado' | 'severo',
  comCinto: boolean,
  aprisionamentosEngine: readonly AprisionamentoMembro[],
): PontoAprisionamento[] {
  const pontos: PontoAprisionamento[] = []
  const ehFrontal = tipoColisao === 'frontal' || tipoColisao === 'frontalComTraseira'
  const ehLateral = tipoColisao === 'lateral' || tipoColisao === 'lateralComCapotamento'
  const ehCapotado =
    tipoColisao === 'capotamento' || tipoColisao === 'tombamento' || tipoColisao === 'lateralComCapotamento'
  const ehMotorista = ocupante?.idAncora === 'motorista' || !ocupante

  // 1. Pedais e Assoalho Dianteiro
  if (ehMotorista) {
    pontos.push({
      local: 'Caixa de Pedais e Parede Corta-Fogo',
      regiaoCorpo: 'Pés e Tornozelos',
      probabilidade: ehFrontal && danoNivel !== 'leve' ? 'alta' : 'moderada',
      descricao:
        'Recuo da parede de fogo e intrusão do conjunto de pedais contra o assoalho dianteiro, prensando calçados e articulações dos tornozelos.',
      condutaResgate:
        'Corte de hastes de pedais com corta-pedal/ferramenta combinada, alívio do assoalho ou tracionamento dianteiro.',
    })
  }

  // 2. Sub-Painel e Barra Transversal
  pontos.push({
    local: 'Painel de Instrumentos Inferior (Dash)',
    regiaoCorpo: 'Joelhos, Coxas e Pernas',
    probabilidade: ehFrontal && danoNivel !== 'leve' ? 'alta' : 'moderada',
    descricao:
      'Colapso da dianteira empurra a viga transversal e o conjunto inferior do painel para trás, travando os joelhos contra o assento do banco.',
    condutaResgate:
      'Técnica de rolamento/elevação de painel (Dash Roll ou Dash Lift) com cilindro de resgate apoiado na base da Coluna B.',
  })

  // 3. Volante e Coluna de Direção
  if (ehMotorista) {
    pontos.push({
      local: 'Volante e Coluna de Direção',
      regiaoCorpo: 'Tórax e Abdômen',
      probabilidade: ehFrontal && (!comCinto || danoNivel === 'severo') ? 'alta' : 'possivel',
      descricao:
        'Intrusão longitudinal da coluna de direção comprimindo o esterno e gradil costal do motorista contra o encosto do banco.',
      condutaResgate: 'Tracionamento da coluna de direção com fita/catraca ou corte dos raios do volante.',
    })
  }

  // 4. Porta Lateral e Coluna B
  pontos.push({
    local: 'Porta Lateral, Fechadura e Coluna B',
    regiaoCorpo: 'Pelve, Quadril e Flanco',
    probabilidade: ehLateral || danoNivel === 'severo' ? 'alta' : 'moderada',
    descricao:
      'Deformação da chapa lateral e bloqueio da fechadura mecânica; intrusão da porta sobre o quadril reduzindo o espaço de sobrevivência.',
    condutaResgate:
      'Desencarceramento lateral: abertura forçada com alargador na fechadura/dobradiças, rebatimento ou retirada da porta e corte da Coluna B.',
  })

  // 5. Teto e Colunas Superiores
  if (ehCapotado) {
    pontos.push({
      local: 'Teto Rebaixado e Colunas A/B/C',
      regiaoCorpo: 'Cabeça, Pescoço e Tronco Superior',
      probabilidade: 'alta',
      descricao:
        'Esmagamento das colunas sob o peso invertido do veículo no solo; teto rebaixado prende a vítima contra o encosto ou a mantém suspensa no cinto de cabeça para baixo.',
      condutaResgate:
        'Estabilização obrigatória (calços/escoras), alívio de peso, rebatimento total do teto ou criação de túnel de extração traseiro.',
    })
  }

  // Itens detectados pela engine de física
  if (aprisionamentosEngine.length > 0) {
    for (const apr of aprisionamentosEngine) {
      const pecas = apr.alvos.length > 0 ? apr.alvos.join(', ') : 'estrutura veicular'
      pontos.push({
        local: `Zona de retenção: ${pecas}`,
        regiaoCorpo: apr.membro,
        probabilidade: 'alta',
        descricao: `Contato sustentado com retenção estrutural detectado contra ${pecas}.`,
        condutaResgate: 'Inspeção visual direta, alívio de deformação ou corte da peça interveniente.',
      })
    }
  }

  return pontos
}

export function PainelCinematica({ quadro, aoAlternar, ...props }: Props) {
  const exibir = (secao: SecaoPainelCinematica) => !props.secoes || props.secoes.includes(secao)
  const definicao = colisao(props.tipoColisao)
  const retido = props.reacaoFoco?.retido ?? quadro.cintoAtivo
  const dano = props.danoVeicular ?? calcularDanoVeicular(props.tipoColisao, props.alvoImpacto, props.velocidadeImpacto)
  const impactos = impactosAte(props.impactos, quadro.tempo)
  const atencoes =
    props.ocupanteFoco?.tipo === 'pessoa'
      ? avaliarAtencaoCorporal(props.impactos, props.ocupanteFoco.id, quadro.tempo)
      : []
  const tipoDeImpacto = leituraDoTipoDeImpacto(props.tipoColisao, props.alvoImpacto)
  const aprisionamentosFoco = props.aprisionamentos.filter(
    (item) =>
      item.idOcupante === props.ocupanteFoco?.id &&
      item.tempo <= quadro.tempo,
  )
  const sequenciaImpactos = classificarSequenciaDeImpactos(
    props.tipoColisao,
    props.alvoImpacto,
    props.impactos,
    props.ocupantes,
    quadro.tempo,
  )
  const grausEncarceramento = classificarEncarceramentos(dano, aprisionamentosFoco)

  const dadosBio = props.ocupanteFoco ? props.estresseBiomecanico?.[props.ocupanteFoco.id] : undefined
  const quadrosBio = dadosBio?.quadros
  const indiceBio = quadrosBio && quadrosBio.length > 0
    ? Math.min(Math.round(quadro.tempo * (quadrosBio.length - 1)), quadrosBio.length - 1)
    : -1
  const leituraBio = indiceBio >= 0 ? quadrosBio![indiceBio] : undefined

  // Métricas de engenharia e biomecânica (Fase 1)
  const comCinto = props.ocupanteFoco?.tipo === 'pessoa' ? props.ocupanteFoco.retencao === 'comCinto' : retido
  const metricasDeltaV = calcularDeltaV(props.tipoColisao, props.alvoImpacto, props.velocidadeImpacto, {
    ehEV: props.ehEV,
    ehBlindado: props.ehBlindado,
  })
  const pulsoAceleracao = calcularPulsoAceleracao(props.tipoColisao, props.velocidadeImpacto, comCinto, quadro.tempo, {
    ehBlindado: props.ehBlindado,
    ehEV: props.ehEV,
    trajetoria: props.trajetoriaFrontal,
  })
  const sindromeCinto = avaliarSindromeCinto(props.ocupanteFoco?.retencao ?? (retido ? 'comCinto' : 'semCinto'), {
    velocidadeKmh: props.velocidadeImpacto,
  })
  const itensXabcde = obterCorrelacaoXABCDE(
    props.tipoColisao,
    metricasDeltaV.deltaVKmh,
    comCinto,
    props.trajetoriaFrontal ?? 'upAndOver',
    props.tonusOcupante,
  )

  // Pistas de cena (Fase 2)
  const pistas = props.pistas ?? obterPistasAtivas(
    props.tipoColisao,
    props.velocidadeImpacto,
    comCinto,
    props.trajetoriaFrontal ?? 'upAndOver',
  )

  const possiveisLesoes = obterPossiveisLesoes(
    props.tipoColisao,
    props.ocupanteFoco,
    props.ocupantes,
    comCinto,
    props.velocidadeImpacto,
    sindromeCinto,
    quadro.rotacaoVeiculoRad.z,
    leituraBio,
  )

  const pontosAprisionamento = obterPontosAprisionamento(
    props.tipoColisao,
    props.ocupanteFoco,
    dano.nivel,
    comCinto,
    aprisionamentosFoco,
  )

  return (
    <>
      {/* Card didático de pista quando selecionada na cena 3D */}
      {props.pistaSelecionada && (
        <CardPistaCena
          pista={props.pistaSelecionada}
          aoFechar={() => props.aoSelecionarPista?.(null)}
        />
      )}

      {exibir('evento') && (
        <>
          <div className="grupo-controle">
            <span className="titulo-controle">Tipo de colisão</span>
            <div className="grade-colisoes">
              {TIPOS_COLISAO.map((tipo) => (
                <button
                  key={tipo}
                  type="button"
                  className={props.tipoColisao === tipo ? 'ativo' : ''}
                  onClick={() => props.aoEscolherTipo(tipo)}
                >
                  {colisao(tipo).nome}
                </button>
              ))}
            </div>
            <p className="dica">{definicao.descricao}</p>
            <p className="classificacao-impacto">
              <strong>
                {tipoDeImpacto.classificacao
                  ? `${tipoDeImpacto.classificacao}: ${tipoDeImpacto.titulo.toLocaleLowerCase('pt-BR')}`
                  : tipoDeImpacto.titulo}
              </strong>
              {tipoDeImpacto.leitura}
            </p>
          </div>

          <div className="grupo-controle">
            <span className="titulo-controle">Velocidade e energia</span>
            <CardFormulaCinematica
              velocidadeKmh={props.velocidadeImpacto}
              massaKg={1200 + (props.ehEV ? 300 : 0) + (props.ehBlindado ? 250 : 0)}
              aoEscolherVelocidade={props.aoEscolherVelocidade}
            />

            <p className="dica">
              {definicao.direcaoImpacto && definicao.quadros[0]!.velocidadeVeiculo === 0
                ? 'Velocidade com que o outro veículo chega. A energia cresce com o QUADRADO dela: dobrar de 30 para 60 km/h quadruplica o que o corpo carrega.'
                : 'Velocidade com que o veículo entra no evento. A energia cresce com o QUADRADO dela: dobrar de 30 para 60 km/h quadruplica o que o corpo carrega.'}
            </p>
          </div>

          {temAlvoExterno(props.tipoColisao) && (
            <div className="grupo-controle">
              <span className="titulo-controle">O que foi atingido</span>
              <div className="grade-alvos-impacto">
                {alvosDoTipo(props.tipoColisao).map((tipo) => (
                  <button
                    key={tipo}
                    type="button"
                    className={props.alvoImpacto === tipo ? 'ativo' : ''}
                    onClick={() => props.aoEscolherAlvo(tipo)}
                  >
                    {alvoImpacto(tipo).nome}
                  </button>
                ))}
              </div>
              <p className="dica">{alvoImpacto(props.alvoImpacto).descricao}</p>
            </div>
          )}

          {props.alvoImpacto === 'outroVeiculo' && props.outroVeiculo && props.aoAlterarOutroVeiculo && (
            <SeletorOutroVeiculo
              tipoColisao={props.tipoColisao}
              config={props.outroVeiculo}
              aoAlterar={props.aoAlterarOutroVeiculo}
              leitura={props.leituraEntreVeiculos}
            />
          )}

          {/* Variações cinematográficas realistas e presets multicorpos (Fases 3 e 4) */}
          <SeletorVariacoesCinematica
            tipoColisao={props.tipoColisao}
            comCinto={comCinto}
            trajetoriaFrontal={props.trajetoriaFrontal ?? 'upAndOver'}
            smallOverlap={props.smallOverlap ?? false}
            ladoLateral={props.ladoLateral ?? 'nearSide'}
            aoAlterarTrajetoriaFrontal={props.aoAlterarTrajetoriaFrontal ?? (() => {})}
            aoAlternarSmallOverlap={props.aoAlternarSmallOverlap ?? (() => {})}
            aoAlterarLadoLateral={props.aoAlterarLadoLateral ?? (() => {})}
            aoAplicarPresetMulticorpo={props.aoAplicarPresetMulticorpo ?? (() => {})}
            blindado={props.ehBlindado ?? false}
            aoAlternarBlindado={props.aoAlternarBlindado}
          />
        </>
      )}

      {exibir('fisica') && (
        <>
          <div className="grupo-controle">
            <span className="titulo-controle">Tônus muscular do ocupante</span>
            <div className="grade-tonus">
              <button
                type="button"
                className={props.tonusOcupante === 'alerta' ? 'ativo' : ''}
                onClick={() => props.aoEscolherTonus('alerta')}
              >
                Alerta (tônus ativo)
              </button>
              <button
                type="button"
                className={props.tonusOcupante === 'inconsciente' ? 'ativo' : ''}
                onClick={() => props.aoEscolherTonus('inconsciente')}
              >
                Inconsciente (flácido)
              </button>
            </div>
            <p className="dica">
              {props.tonusOcupante === 'alerta'
                ? 'Ocupante consciente com rigidez e tônus ativo: resiste à inércia inicial com a musculatura cervical e paravertebral, reduzindo o efeito chicote primário.'
                : 'Ocupante inconsciente ou relaxado: ausência de contração reflexa; a desaceleração atua livremente sobre ligamentos e a coluna cervical, amplificando o chicote cervical e o risco de TRM.'}
            </p>
          </div>

          <div className="grupo-controle">
            <span className="titulo-controle">Física da simulação</span>
            <label className="toggle">
              <input
                type="checkbox"
                checked={props.fisicaReal}
                onChange={(e) => props.aoAlternarFisica(e.target.checked)}
              />
              física dos ocupantes e objetos
            </label>
            {props.fisicaReal && props.estadoFisica === 'carregando' && (
              <p className="dica">Carregando o motor de física…</p>
            )}
            {props.fisicaReal && props.estadoFisica === 'indisponivel' && (
              <p className="obstaculo apertado">
                <strong>Física real indisponível</strong>
                O motor não carregou. A cena seguiu pelo caminho procedural: a carga anda em linha
                reta, sem gravidade e sem tombar.
              </p>
            )}
            {props.fisicaReal && props.estadoFisica === 'pronta' && impactos.length === 0 && (
              <p className="dica">
                Massa, volume e gravidade valendo para ocupantes e carga. Nenhum corpo encontrou
                obstáculo com força suficiente para registrar impacto neste evento.
              </p>
            )}
          </div>
        </>
      )}

      {/* Seção dedicada de Métricas Físicas e Protocolo XABCDE (Fase 1) */}
      {exibir('metricas') && (
        <PainelMetricasXABCDE
          metricasDeltaV={metricasDeltaV}
          pulsoAceleracao={pulsoAceleracao}
          tempoNormalizado={quadro.tempo}
          comCinto={comCinto}
          sindromeCinto={sindromeCinto}
          itensXabcde={itensXabcde}
        />
      )}

      {/* Seção dedicada de Investigação Forense / CSI do Resgate (Fase 5) */}
      {exibir('investigador') && (
        <PainelInvestigadorCSI
          pistasDisponiveis={pistas}
          pistasInspecionadas={props.pistasInspecionadas ?? []}
          pistaSelecionada={props.pistaSelecionada ?? null}
          aoSelecionarPista={props.aoSelecionarPista ?? (() => {})}
          cenarioReal={{
            tipoColisao: props.tipoColisao,
            faixaDeltaV: metricasDeltaV.classificacao,
            deltaVKmh: metricasDeltaV.deltaVKmh,
            prioridadesCorretas: itensXabcde.filter((i) => i.prioridade === 'critica').map((i) => i.letra),
            comCinto,
            trajetoriaFrontal: props.trajetoriaFrontal,
          }}
          aoVerReplay={props.aoVerReplay ?? (() => {})}
          aoExportarParaOficina={props.aoExportarParaOficina ?? (() => {})}
        />
      )}

      {exibir('sobreposicoes') && (
        <div className="grupo-controle">
          <span className="titulo-controle">Sobreposições</span>
          <label className="toggle">
            <input
              type="checkbox"
              checked={props.mostrarVetores}
              onChange={(e) => aoAlternar('mostrarVetores', e.target.checked)}
            />
            vetores de deslocamento e impacto
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={props.mostrarRastros}
              onChange={(e) => aoAlternar('mostrarRastros', e.target.checked)}
            />
            rastros de cabeça e tronco
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={props.mostrarEixoColuna}
              onChange={(e) => aoAlternar('mostrarEixoColuna', e.target.checked)}
            />
            eixo da coluna
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={props.mostrarPistas !== false}
              onChange={(e) => props.aoAlternarMostrarPistas?.(e.target.checked)}
            />
            pistas de cena e hotspots de trauma
          </label>
          {props.aoAlternarMapaDeformacao && (
            <label className="toggle">
              <input
                type="checkbox"
                checked={props.mostrarMapaDeformacao ?? false}
                onChange={(e) => props.aoAlternarMapaDeformacao?.(e.target.checked)}
              />
              mapa de deformação (zona, intensidade, direção da carga)
            </label>
          )}
          {props.aoAlternarLataSolida && (
            <label className="toggle">
              <input
                type="checkbox"
                checked={props.lataSolida ?? false}
                onChange={(e) => props.aoAlternarLataSolida?.(e.target.checked)}
              />
              lataria sólida (amassamentos + dano estrutural em raio-X)
            </label>
          )}
        </div>
      )}

      {exibir('resultados') && (
        <div className="grupo-controle leitura">
          <span className="titulo-controle">
            Instante: Lesões e Aprisionamento {props.ocupanteFoco ? `: ${props.ocupanteFoco.nome}` : ''}
          </span>
          <p className="descricao-fase">{quadro.descricao}</p>

          <div className="bloco-lesoes">
            <span className="titulo-controle titulo-controle--perigo">Possíveis lesões</span>
            <ul className="lista-cartoes-lesao">
              {possiveisLesoes.map((lesao, idx) => (
                <li key={idx} className={`cartao-lesao cartao-lesao--${lesao.gravidade}`}>
                  <div className="cartao-lesao__cabecalho">
                    <strong>{lesao.titulo}</strong>
                    <span className={`tag-gravidade-lesao tag-gravidade-lesao--${lesao.gravidade}`}>
                      {lesao.gravidade.toUpperCase()}
                    </span>
                  </div>
                  <span className="cartao-lesao__regiao">{lesao.regiao}</span>
                  <p>{lesao.descricao}</p>
                </li>
              ))}
            </ul>
          </div>

          {pontosAprisionamento.length > 0 && (
            <div className="bloco-lesoes">
              <span className="titulo-controle titulo-controle--alerta">Onde pode ter ficado preso</span>
              <ul className="lista-cartoes-lesao">
                {pontosAprisionamento.map((ponto, idx) => (
                  <li key={idx} className={`cartao-lesao cartao-lesao--aprisionamento cartao-lesao--prob-${ponto.probabilidade}`}>
                    <div className="cartao-lesao__cabecalho">
                      <strong>{ponto.local}</strong>
                      <span className={`tag-gravidade-lesao tag-gravidade-lesao--prob-${ponto.probabilidade}`}>
                        {ponto.probabilidade.toUpperCase()}
                      </span>
                    </div>
                    <span className="cartao-lesao__regiao">{ponto.regiaoCorpo}</span>
                    <p>{ponto.descricao}</p>
                  </li>
                ))}
              </ul>
              {/* A conduta costuma ser a mesma para vários pontos: repetida em
                  cada cartão, ela virava ruído. Aparece uma vez por conduta. */}
              {[...new Set(pontosAprisionamento.map((ponto) => ponto.condutaResgate))].map((conduta) => (
                <p key={conduta} className="nota-desencarceramento">
                  <strong>Desencarceramento:</strong> {conduta}
                </p>
              ))}
            </div>
          )}

          <p className="obstaculo bloqueio">
            <strong>Deformação estrutural didática</strong>
            {dano.leitura}
          </p>
          {haContatoPelaTraseira(props.tipoColisao) && (
            <p className="dica">
              Nenhum airbag é acionado num contato traseiro. O dispositivo que limita a defasagem entre
              cabeça e tronco é o apoio de cabeça (altura e distância dele são o que importa aqui).
            </p>
          )}
        </div>
      )}
    </>
  )
}
